/**
 * `ShareLinkSettings` — GVW-146 share-link permissions.
 *
 * Per brief §2 / §5: defaults the `tier` (editable / readonly), `auditOnShare`
 * toggle, and recipient `allowedDomains` allowlist. Persists via localStorage
 * fallback under `gvw-set-share-link` per dispatch §1 (platform-sdk lacks a
 * share-defaults endpoint in this stub).
 *
 * TODO(WP-E10): when `@geosoft/platform-sdk` exposes a real share-settings
 * API, swap `localStorage` for `sdk.shareSettings.set(...)` here.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-146/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useDefenceVariant } from '@/design/hooks';
import { useLiveRegion } from '@/state/liveRegion';

interface ShareDefaults {
  tier: 'editable' | 'readonly';
  auditOnShare: boolean;
  allowedDomains: string[];
}

const STORAGE_KEY = 'gvw-set-share-link';
const DEFAULTS: ShareDefaults = { tier: 'readonly', auditOnShare: true, allowedDomains: [] };

export function ShareLinkSettings() {
  const liveRegion = useLiveRegion();
  const defenceVariant = useDefenceVariant();
  const [settings, setSettings] = useState<ShareDefaults>(DEFAULTS);
  const [domain, setDomain] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) setSettings(JSON.parse(stored) as ShareDefaults);
    } catch {
      /* fall back to defaults */
    }
  }, []);

  useEffect(() => {
    document.title = 'Share-link settings · GeoViewer';
    liveRegion.announce('status', 'Share-link settings loaded.');
  }, [liveRegion]);

  const handleSave = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setSaved(true);
      setError(null);
      liveRegion.announce('status', 'Share defaults saved. An audit row will be written on the next share.');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not save settings';
      setError(message);
      liveRegion.announce('alert', message);
    }
  }, [settings, liveRegion]);

  const addDomain = useCallback(() => {
    const next = domain.trim().toLowerCase();
    if (!next) return;
    if (settings.allowedDomains.includes(next)) return;
    setSettings((prev) => ({ ...prev, allowedDomains: [...prev.allowedDomains, next] }));
    setDomain('');
  }, [domain, settings.allowedDomains]);

  const removeDomain = useCallback((d: string) => {
    setSettings((prev) => ({ ...prev, allowedDomains: prev.allowedDomains.filter((x) => x !== d) }));
  }, []);

  const isLocked = defenceVariant.active;

  return (
    <main
      role="main"
      aria-labelledby="gvw-146-heading"
      data-gvw-id="GVW-146"
      data-route="/workspace/settings/share-link"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-146-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          share
        </span>
        Share-link settings
      </h1>

      {isLocked ? (
        <div
          role="status"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.warning}`,
            borderRadius: tokens.radius.md,
            color: tokens.color.ink.primary,
          }}
        >
          Defence-variant cross-cluster recipients require policy approval. Form is read-only.
        </div>
      ) : null}

      {saved ? (
        <div
          role="status"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.success}`,
            borderRadius: tokens.radius.md,
          }}
        >
          Settings saved.
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.danger}`,
            borderRadius: tokens.radius.md,
          }}
        >
          Could not save: {error}
        </div>
      ) : null}

      <form
        aria-labelledby="gvw-146-heading"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[5], maxWidth: 640 }}
      >
        <fieldset
          disabled={isLocked}
          style={{ border: 'none', padding: 0, margin: 0 }}
        >
          <legend style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[2] }}>
            Default share tier
          </legend>
          <div role="radiogroup" aria-label="Default share tier" style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}>
            <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
              <input
                type="radio"
                name="tier"
                value="editable"
                checked={settings.tier === 'editable'}
                onChange={() => setSettings((p) => ({ ...p, tier: 'editable' }))}
              />
              Editable
            </label>
            <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
              <input
                type="radio"
                name="tier"
                value="readonly"
                checked={settings.tier === 'readonly'}
                onChange={() => setSettings((p) => ({ ...p, tier: 'readonly' }))}
              />
              Read-only (recommended)
            </label>
          </div>
        </fieldset>

        <label style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          <input
            type="checkbox"
            role="switch"
            aria-checked={settings.auditOnShare}
            checked={settings.auditOnShare}
            disabled={isLocked}
            onChange={(e) => setSettings((p) => ({ ...p, auditOnShare: e.target.checked }))}
          />
          Write an audit row whenever a workspace is shared
        </label>

        <div>
          <label
            htmlFor="gvw-146-domain"
            style={{ display: 'block', fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[2] }}
          >
            Recipient domain allowlist
          </label>
          <div style={{ display: 'flex', gap: tokens.space[2] }}>
            <input
              id="gvw-146-domain"
              type="text"
              value={domain}
              disabled={isLocked}
              placeholder="example.com"
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDomain();
                }
              }}
              style={{
                flex: 1,
                padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
                background: tokens.color.surface[1],
                color: tokens.color.ink.primary,
                border: tokens.border.thin,
                borderRadius: tokens.radius.input,
                fontSize: tokens.font.size.md,
              }}
            />
            <button
              type="button"
              onClick={addDomain}
              disabled={isLocked || !domain.trim()}
              style={{
                padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
                background: tokens.color.surface[2],
                color: tokens.color.ink.primary,
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
                fontSize: tokens.font.size.md,
                cursor: 'pointer',
              }}
            >
              Add
            </button>
          </div>
          {settings.allowedDomains.length === 0 ? (
            <p style={{ marginTop: tokens.space[2], color: tokens.color.ink.muted, fontSize: tokens.font.size.sm }}>
              No restrictions yet. Add a domain to limit share recipients.
            </p>
          ) : (
            <ul style={{ marginTop: tokens.space[2], padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: tokens.space[2] }}>
              {settings.allowedDomains.map((d) => (
                <li
                  key={d}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: tokens.space[2],
                    padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
                    background: tokens.color.surface[2],
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.chip,
                    fontSize: tokens.font.size.sm,
                  }}
                >
                  {d}
                  <button
                    type="button"
                    aria-label={`Remove ${d}`}
                    onClick={() => removeDomain(d)}
                    disabled={isLocked}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: tokens.color.ink.secondary,
                      cursor: 'pointer',
                      fontSize: tokens.font.size.md,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLocked}
            style={{
              padding: `${tokens.space[3]}px ${tokens.space[5]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: 'none',
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              cursor: isLocked ? 'not-allowed' : 'pointer',
              opacity: isLocked ? 0.6 : 1,
            }}
          >
            Save settings
          </button>
        </div>
      </form>
    </main>
  );
}
